// src/components/employee-steps/StepEmployment.js

/**
 * StepEmployment Component - Add Employee wizard step for employment details:
 * department, start date, work schedule, and the government-contribution inputs
 * (SSS/Pag-IBIG/PhilHealth/Tax). Salary lives in the Sensitive Info step (PII).
 * With PII access this runs AFTER Sensitive Info (BL-063 salary-first reorder),
 * so the SSS input's suggestion chip can read the already-entered salary; without
 * it, this is step 2 and the chip is absent.
 * Features enhanced UI with searchable dropdowns and date format confirmation.
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import Icon from "../icons/Icon";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import useDepartments from "../../hooks/useDepartments";
import PwPopover from "../PwPopover";
import ContributionBasisNote from "../ContributionBasisNote";
import {
  fetchSssTableVersions,
  resolveInForceVersion,
  suggestEmployeeSss,
  monthlyGrossForEmployee,
} from "../../services/sssContributionTable";
import {
  fetchPhilhealthTableVersions,
  suggestEmployeePhilhealth,
} from "../../services/philhealthContributionTable";
import {
  fetchPagibigTableVersions,
  computePagibigShares,
} from "../../services/pagibigContributionTable";

export const StepEmployment = ({
  formData,
  handleInputChange,
  errors,
  handleNext,
  handlePrevious,
  isEdit = false,
  employeeDocId = null,
  refreshData = null,
  scheduleDefaults = { startTime: "08:00", endTime: "17:00" },
  nextStepLabel = "Next →",
  onClearEndDate = null,
}) => {
  const firstInputRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);
  const { company } = useAuth();
  const navigate = useNavigate();
  const { departments, loading: deptLoading } = useDepartments();

  // Fetch hoursPerDay for OT/UT schedule info — attendancePolicy is the home
  // (C4); payrollRates is the pre-backfill fallback window.
  const [hoursPerDay, setHoursPerDay] = useState(null);
  useEffect(() => {
    if (!company?.id) return;
    getDoc(doc(db, 'companies', company.id, 'settings', 'attendancePolicy'))
      .then(async snap => {
        const fromPolicy = snap.exists() ? snap.data().hoursPerDay : undefined;
        if (typeof fromPolicy === 'number') { setHoursPerDay(fromPolicy); return; }
        const rates = await getDoc(doc(db, 'companies', company.id, 'settings', 'payrollRates'));
        setHoursPerDay(rates.exists() ? (rates.data().hoursPerDay ?? 8) : 8);
      })
      .catch(() => {});
  }, [company?.id]);

  // BL-063: resolve the in-force SSS contribution-table version (BL-062) + the
  // company's monthly→daily rate factor so the SSS input can offer a
  // salary-derived suggestion chip. Salary is entered in the prior (Sensitive
  // Info) step, so it's available here. Any read failure or empty collection
  // just leaves the chip absent — manual entry (the fallback contract).
  const [sssVersion, setSssVersion] = useState(null);
  const [philhealthVersion, setPhilhealthVersion] = useState(null);
  const [pagibigVersion, setPagibigVersion] = useState(null);
  const [dailyRateFactor, setDailyRateFactor] = useState(null);
  // PRD_SssContributionBasis C14: when the company composes its SSS basis from
  // actual pay, payroll no longer reads the stored figure, so the field gets a
  // note saying so. Read off the SAME payrollRates snapshot as dailyRateFactor.
  const [sssBasisEnabled, setSssBasisEnabled] = useState(false);
  // H11: the PhilHealth twin, off the SAME snapshot.
  const [philhealthBasisEnabled, setPhilhealthBasisEnabled] = useState(false);
  useEffect(() => {
    if (!company?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const versions = await fetchSssTableVersions();
        if (!cancelled) setSssVersion(resolveInForceVersion(versions));
      } catch { /* no version → no chip, manual entry */ }
      // BL-073: same effect resolves the in-force PhilHealth version for the
      // parallel PhilHealth suggestion chip (independent of SSS availability).
      try {
        const phVersions = await fetchPhilhealthTableVersions();
        if (!cancelled) setPhilhealthVersion(resolveInForceVersion(phVersions));
      } catch { /* no version → no chip, manual entry */ }
      try {
        const rates = await getDoc(
          doc(db, 'companies', company.id, 'settings', 'payrollRates')
        );
        if (!cancelled) {
          // Use the company's CONFIGURED Rate Calculation factor (365/313/261/
          // custom). 365 is only the last-resort default when a company has
          // never set one — the same default the payroll engine itself uses
          // (DEFAULT_PAYROLL_RATES.dailyRateFactor), so the suggestion stays
          // consistent with actual pay.
          setDailyRateFactor(rates.exists() ? (rates.data().dailyRateFactor ?? 365) : 365);
          setSssBasisEnabled(rates.exists() && rates.data().sssBasis?.enabled === true);
          setPhilhealthBasisEnabled(
            rates.exists() && rates.data().philhealthBasis?.enabled === true
          );
        }
      } catch { /* read failed → factor stays null → daily employees get no chip */ }
      // BL-074: the Pag-IBIG version resolves LAST, deliberately — dailyRateFactor
      // (read just above) gates every daily-rate chip, and this is a serial chain,
      // so fetching pagibig ahead of it would delay all three chips.
      try {
        const pgVersions = await fetchPagibigTableVersions();
        if (!cancelled) setPagibigVersion(resolveInForceVersion(pgVersions));
      } catch { /* no version → no chip, manual entry */ }
    })();
    return () => { cancelled = true; };
  }, [company?.id]);

  // Live-derived SSS suggestion (peso figure or null). Recomputes as salary
  // changes; it is a hint only and never writes the saved value.
  const suggestedSss = useMemo(
    () =>
      suggestEmployeeSss({
        version: sssVersion,
        salaryType: formData.employment?.salaryType,
        monthlySalary: formData.employment?.monthlySalary,
        dailySalary: formData.employment?.dailySalary,
        dailyRateFactor,
      }),
    [
      sssVersion,
      dailyRateFactor,
      formData.employment?.salaryType,
      formData.employment?.monthlySalary,
      formData.employment?.dailySalary,
    ]
  );

  // BL-073: live-derived PhilHealth suggestion (peso figure or null), the twin
  // of suggestedSss. Recomputes as salary changes; hint only, never auto-writes.
  const suggestedPhilhealth = useMemo(
    () =>
      suggestEmployeePhilhealth({
        version: philhealthVersion,
        salaryType: formData.employment?.salaryType,
        monthlySalary: formData.employment?.monthlySalary,
        dailySalary: formData.employment?.dailySalary,
        dailyRateFactor,
      }),
    [
      philhealthVersion,
      dailyRateFactor,
      formData.employment?.salaryType,
      formData.employment?.monthlySalary,
      formData.employment?.dailySalary,
    ]
  );

  // The monthly compensation the suggestion is bracketed against — surfaced in
  // the info popover so the derivation is transparent (esp. the daily path,
  // which uses the company's configured Rate Calculation factor).
  const suggestionGross = useMemo(
    () =>
      monthlyGrossForEmployee({
        salaryType: formData.employment?.salaryType,
        monthlySalary: formData.employment?.monthlySalary,
        dailySalary: formData.employment?.dailySalary,
        dailyRateFactor,
      }),
    [
      dailyRateFactor,
      formData.employment?.salaryType,
      formData.employment?.monthlySalary,
      formData.employment?.dailySalary,
    ]
  );

  // BL-074: ONE memo serves both the Pag-IBIG chip and its popover — it returns
  // {ee, er, base, band}, so the popover reads `base` (the MFS clamp) and `band`
  // (the selected rate) off the same object rather than re-deriving the band rule
  // in JSX. Reuses the shared suggestionGross above (the gross is
  // table-independent). C13: that gross is BASIC monthly salary, while HDMF's
  // "Fund Salary" also includes allowances — Sahod has no allowances field at
  // setup, so the popover discloses the basis and the field stays editable.
  const pagibigShares = useMemo(
    () => computePagibigShares(pagibigVersion, suggestionGross),
    [pagibigVersion, suggestionGross]
  );

  // BL-073: the PhilHealth premium is computed on the income CLAMPED to the
  // version's floor/ceiling, so for a salary outside that band the suggested
  // figure deliberately won't equal `rate × salary`. Name the bound that applied
  // — but ONLY when the clamp actually bites, so in the common case the popover
  // stays as short as the SSS one. null = no clamp note.
  const philhealthClampNote = useMemo(() => {
    const p = philhealthVersion?.params;
    if (!p || suggestionGross === null) return null;
    if (suggestionGross < p.incomeFloor) {
      return { bound: "minimum", amount: p.incomeFloor };
    }
    if (suggestionGross > p.incomeCeiling) {
      return { bound: "maximum", amount: p.incomeCeiling };
    }
    return null;
  }, [philhealthVersion, suggestionGross]);

  // Compute OT/UT implied by the current work schedule
  const scheduleOtUt = useMemo(() => {
    if (hoursPerDay == null) return null;
    const schedule = formData.employment?.workSchedule || {};
    const firstWorkDay = Object.values(schedule).find(d => d?.isWorkingDay);
    if (!firstWorkDay?.startTime || !firstWorkDay?.endTime) return null;

    const [sh, sm] = firstWorkDay.startTime.split(':').map(Number);
    const [eh, em] = firstWorkDay.endTime.split(':').map(Number);
    let shiftMinutes = (eh * 60 + em) - (sh * 60 + sm);
    if (shiftMinutes <= 0) shiftMinutes += 24 * 60; // cross-midnight
    const breakMin = firstWorkDay.breakDuration || 0;
    const paidHours = (shiftMinutes - breakMin) / 60;

    const ot = Math.max(0, Math.round((paidHours - hoursPerDay) * 100) / 100);
    const ut = Math.max(0, Math.round((hoursPerDay - paidHours) * 100) / 100);
    const regularHours = Math.min(hoursPerDay, Math.round(paidHours * 100) / 100);
    return { ot, ut, regularHours };
  }, [formData.employment?.workSchedule, hoursPerDay]);


  // Calculate fullName from personal info
  const getFullName = () => {
    const { firstName, middleName, lastName } = formData.personalInfo || {};
    const parts = [firstName, middleName, lastName].filter(Boolean);
    return parts.join(" ").trim();
  };

  /**
   * Format date to Filipino format (Month DD, YYYY)
   */
  const formatDateToFilipino = (dateString) => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      const options = { year: "numeric", month: "short", day: "numeric" };
      return date.toLocaleDateString("en-US", options);
    } catch (error) {
      return "";
    }
  };

  // Format a 'YYYY-MM-DD' effective date to 'Mmm dd, yyyy' (BL-063 SSS popover).
  // Built from string parts — no Date/timezone, so no calendar-date drift.
  const formatEffectiveDate = (ymd) => {
    if (!ymd || typeof ymd !== "string") return "";
    const [y, m, d] = ymd.split("-");
    const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mi = parseInt(m, 10) - 1;
    return mi >= 0 && mi <= 11 ? `${MONTHS[mi]} ${d}, ${y}` : ymd;
  };

  /**
   * Format salary with peso sign and commas
   */
  const formatSalary = (value) => {
    // Remove non-numeric characters except decimal
    const numericValue = value.toString().replace(/[^0-9.]/g, "");

    // Format with commas
    if (numericValue) {
      const parts = numericValue.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return parts.join(".");
    }

    return "";
  };

  // Salary handling moved to StepSensitiveInfo per v2.0 PII split.
  // The old force-to-daily mount effect is gone (PRD_MonthlySalaryType):
  // salaryType is a real choice on Step 3 now; the wizard default is 'daily'.
  useEffect(() => {
    if (!formData.employment?.salaryType) {
      handleInputChange("employment", "salaryType", "daily");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Auto-populate account name with fullName if empty
   */
  useEffect(() => {
    const fullName = getFullName();
    if (fullName && !formData.banking?.accountName) {
      handleInputChange("banking", "accountName", fullName);
    }
  }, [formData.personalInfo?.firstName, formData.personalInfo?.middleName, formData.personalInfo?.lastName]);

  /**
   * Check if form is valid for enabling Next button
   */
  const isFormValid = () => {
    // Check if start date is filled
    if (!formData.employment?.startDate) {
      return false;
    }

    // Salary + bank account moved to Step 3 (Sensitive Info) per v2.0.

    // Check if government contributions are valid (can be 0 but not empty).
    // Contributions stay on the parent doc — DTR encoders need to read them.
    const sssContribution = formData.employment?.sssContribution;
    const pagibigContribution = formData.employment?.pagibigContribution;
    const philhealthContribution = formData.employment?.philhealthContribution;
    const withholdingTax = formData.employment?.withholdingTax;

    if (sssContribution === undefined || sssContribution === '' ||
        pagibigContribution === undefined || pagibigContribution === '' ||
        philhealthContribution === undefined || philhealthContribution === '' ||
        withholdingTax === undefined || withholdingTax === '') {
      return false;
    }

    // Check if at least one working day is selected
    const workSchedule = formData.employment?.workSchedule;
    if (workSchedule) {
      const hasWorkingDay = Object.values(workSchedule).some(day => day?.isWorkingDay);
      if (!hasWorkingDay) {
        return false;
      }
      // Check that working days have startTime and endTime
      const workingDays = Object.values(workSchedule).filter(day => day?.isWorkingDay);
      if (workingDays.some(day => !day.startTime || !day.endTime)) {
        return false;
      }
    }

    return true;
  };

  return (
    <div className="add-employee-form-section">
      {/* Employment Details Container */}
      <div className="employment-details-container">
        {/* Section Header */}
        <div className="form-section-header">
          <div className="form-section-icon"><Icon name="briefcase" /></div>
          <h2 className="form-section-title">Employment Details</h2>
        </div>

        {/* Form Fields */}
        <div className="employment-form-fields">
          {/* Salary fields moved to Step 3 (Sensitive Info) per v2.0 PII split.
              Salary lives in the PII subcollection, not on the parent doc. */}

          {/* Row: Government Deductions (SSS, Pag-IBIG, PhilHealth, Tax)
              These STAY on the parent employee doc — DTR encoders need to
              read them with emp:view to stamp on time records. See
              PRD_UAM_CustomRoles2.md §5.2 contributions-on-parent decision. */}
          <div className="employment-form-row employment-form-row--4col">
            {/* SSS Contribution Field */}
            <div className="form-group">
              {/* Label row — the BL-063 info popover sits beside the label so it
                  never takes its own row. Present only when a suggestion exists. */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <label className="form-label" style={{ marginBottom: 0 }}>
                  SSS <span className="required">*</span>
                </label>
                {suggestedSss !== null && (
                  <PwPopover
                    ariaLabel="How the suggested SSS is calculated"
                    align="start"
                    trigger={<span aria-hidden="true" style={{ fontWeight: 700 }}>ⓘ</span>}
                  >
                    <div style={{ maxWidth: "260px", fontSize: "0.8125rem", lineHeight: 1.5, color: "var(--ui-text-secondary)" }}>
                      <strong style={{ display: "block", marginBottom: "4px", color: "var(--ui-text)" }}>
                        Suggested SSS — how it's calculated
                      </strong>
                      {formData.employment?.salaryType === "monthly" ? (
                        <p style={{ margin: "0 0 6px" }}>
                          Employee SSS share for a monthly salary of{" "}
                          <strong>₱{Number(suggestionGross || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>.
                        </p>
                      ) : (
                        <p style={{ margin: "0 0 6px" }}>
                          Daily rate{" "}
                          <strong>₱{Number(formData.employment?.dailySalary || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>{" "}
                          × Rate Calculation factor <strong>{dailyRateFactor}</strong> ÷ 12 ={" "}
                          <strong>₱{Number(suggestionGross || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>/mo.
                        </p>
                      )}
                      <p style={{ margin: 0, color: "var(--ui-text-muted)" }}>
                        {sssVersion?.circular
                          ? `Based on ${sssVersion.circular} effective ${formatEffectiveDate(sssVersion.effectiveDate)}.`
                          : `Based on the SSS contribution table effective ${formatEffectiveDate(sssVersion?.effectiveDate)}.`}
                      </p>
                    </div>
                  </PwPopover>
                )}
              </div>
              <div className="input-group">
                <span className="input-group__addon">₱</span>
                <input
                  type="text"
                  value={formatSalary(formData.employment?.sssContribution || "")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, "");
                    handleInputChange("employment", "sssContribution", value);
                  }}
                  className={`form-input form-input--right ${
                    errors.sssContribution ? "error" : ""
                  }`}
                  placeholder="0.00"
                  style={{ textAlign: "right" }}
                />
              </div>
              {errors.sssContribution && (
                <span className="form-error-text">{errors.sssContribution}</span>
              )}
              {/* BL-063: clickable, salary-derived SSS suggestion (mirrors the
                  payroll Deductions clickable-balance pattern). The input stays
                  the single source of truth; clicking fills it. Absent when no
                  table version resolves or salary is blank (manual entry). */}
              {suggestedSss !== null && (
                <button
                  type="button"
                  tabIndex={0}
                  onClick={() =>
                    handleInputChange("employment", "sssContribution", String(suggestedSss))
                  }
                  title="Click to fill the SSS field with this suggestion"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    marginTop: "6px",
                    padding: "3px 8px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "var(--ui-primary)",
                    background: "var(--ui-primary-tint)",
                    border: "1px solid var(--ui-border)",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  Suggested: ₱{suggestedSss.toLocaleString("en-PH", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} · click to fill
                </button>
              )}
              {/* PRD_SssContributionBasis C14: the input, its validator and the
                  chip all STAY — removing the input would persist
                  sssContribution: 0 on every new employee (AddEmployee.js
                  coerces an absent value). The stored figure is simply not the
                  payroll target while the basis is on, so say so. Locked string,
                  now rendered by the shared note (H11) so the two read-only
                  surfaces can show the same wording. */}
              {sssBasisEnabled && (
                <ContributionBasisNote agencies={["SSS"]} />
              )}
            </div>

            {/* Pag-IBIG Contribution Field */}
            <div className="form-group">
              {/* Label row — BL-074 info popover beside the label (mirrors SSS +
                  PhilHealth). Present only when a suggestion exists. */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <label className="form-label" style={{ marginBottom: 0 }}>
                  Pag-IBIG <span className="required">*</span>
                </label>
                {pagibigShares !== null && (
                  <PwPopover
                    ariaLabel="How the Pag-IBIG mandatory minimum is calculated"
                    align="start"
                    trigger={<span aria-hidden="true" style={{ fontWeight: 700 }}>ⓘ</span>}
                  >
                    <div style={{ maxWidth: "280px", fontSize: "0.8125rem", lineHeight: 1.5, color: "var(--ui-text-secondary)" }}>
                      <strong style={{ display: "block", marginBottom: "4px", color: "var(--ui-text)" }}>
                        Pag-IBIG mandatory minimum — how it's calculated
                      </strong>
                      {/* ONE derivation line: basis → MFS clamp (only when it
                          actually bites) × rate = employee share. Trimmed from
                          seven paragraphs at /test-impl — the long form buried
                          the figure it existed to explain. Every fact C11/C13/C14
                          require is still here, just denser; the band NAME was
                          dropped because the rate and the result carry its
                          meaning, and the standalone "mandatory minimum"
                          paragraph because the chip label already says it. */}
                      <p style={{ margin: "0 0 6px" }}>
                        {formData.employment?.salaryType === "monthly" ? (
                          <strong>₱{Number(suggestionGross || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        ) : (
                          <>
                            <strong>₱{Number(formData.employment?.dailySalary || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                            {" × "}<strong>{dailyRateFactor}</strong>{" ÷ 12 = "}
                            <strong>₱{Number(suggestionGross || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>/mo
                          </>
                        )}
                        {pagibigShares.base < (suggestionGross ?? 0) && (
                          <>
                            {" → capped at "}
                            <strong>₱{Number(pagibigShares.base).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                            {" (MFS)"}
                          </>
                        )}
                        {" × "}
                        <strong>{(pagibigShares.band.eeRate * 100).toFixed(2).replace(/\.00$/, "")}%</strong>
                        {" = "}
                        <strong>₱{pagibigShares.ee.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                      </p>
                      <p style={{ margin: "0 0 6px" }}>
                        Employer adds{" "}
                        <strong>₱{pagibigShares.er.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>{" "}
                        separately — not deducted from the employee.
                      </p>
                      <p style={{ margin: "0 0 6px", color: "var(--ui-text-muted)" }}>
                        Based on basic salary. Add allowances or voluntary top-ups
                        manually.
                      </p>
                      <p style={{ margin: 0, color: "var(--ui-text-muted)" }}>
                        {pagibigVersion?.circular
                          ? `${pagibigVersion.circular} · effective ${formatEffectiveDate(pagibigVersion.effectiveDate)}`
                          : `Pag-IBIG contribution table · effective ${formatEffectiveDate(pagibigVersion?.effectiveDate)}`}
                      </p>
                    </div>
                  </PwPopover>
                )}
              </div>
              <div className="input-group">
                <span className="input-group__addon">₱</span>
                <input
                  type="text"
                  value={formatSalary(formData.employment?.pagibigContribution || "")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, "");
                    handleInputChange("employment", "pagibigContribution", value);
                  }}
                  className={`form-input form-input--right ${
                    errors.pagibigContribution ? "error" : ""
                  }`}
                  placeholder="0.00"
                  style={{ textAlign: "right" }}
                />
              </div>
              {errors.pagibigContribution && (
                <span className="form-error-text">{errors.pagibigContribution}</span>
              )}
              {/* BL-074: clickable, salary-derived Pag-IBIG chip (mirrors the SSS
                  and PhilHealth chips in markup + interaction). The copy
                  deliberately says "Mandatory minimum", NOT "Suggested" —
                  voluntary top-ups above the ₱200 ceiling are common for Pag-IBIG,
                  and finality-implying copy would lead operators to overwrite a
                  deliberate top-up. The input stays the single source of truth;
                  clicking fills it. Absent when no version resolves or the salary
                  is blank (manual entry). */}
              {pagibigShares !== null && (
                <button
                  type="button"
                  tabIndex={0}
                  onClick={() =>
                    handleInputChange("employment", "pagibigContribution", String(pagibigShares.ee))
                  }
                  title="Click to fill the Pag-IBIG field with the mandatory minimum"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    marginTop: "6px",
                    padding: "3px 8px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "var(--ui-primary)",
                    background: "var(--ui-primary-tint)",
                    border: "1px solid var(--ui-border)",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  Mandatory minimum: ₱{pagibigShares.ee.toLocaleString("en-PH", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} · click to fill
                </button>
              )}
            </div>

            {/* PhilHealth Contribution Field */}
            <div className="form-group">
              {/* Label row — BL-073 info popover beside the label (mirrors SSS).
                  Present only when a suggestion exists. */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <label className="form-label" style={{ marginBottom: 0 }}>
                  PhilHealth <span className="required">*</span>
                </label>
                {suggestedPhilhealth !== null && (
                  <PwPopover
                    ariaLabel="How the suggested PhilHealth is calculated"
                    align="start"
                    trigger={<span aria-hidden="true" style={{ fontWeight: 700 }}>ⓘ</span>}
                  >
                    <div style={{ maxWidth: "260px", fontSize: "0.8125rem", lineHeight: 1.5, color: "var(--ui-text-secondary)" }}>
                      <strong style={{ display: "block", marginBottom: "4px", color: "var(--ui-text)" }}>
                        Suggested PhilHealth — how it's calculated
                      </strong>
                      {formData.employment?.salaryType === "monthly" ? (
                        <p style={{ margin: "0 0 6px" }}>
                          Employee PhilHealth share for a monthly salary of{" "}
                          <strong>₱{Number(suggestionGross || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>.
                        </p>
                      ) : (
                        <p style={{ margin: "0 0 6px" }}>
                          Daily rate{" "}
                          <strong>₱{Number(formData.employment?.dailySalary || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>{" "}
                          × Rate Calculation factor <strong>{dailyRateFactor}</strong> ÷ 12 ={" "}
                          <strong>₱{Number(suggestionGross || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>/mo.
                        </p>
                      )}
                      {/* Only when the salary falls outside the version's band —
                          otherwise the figure looks like it ignores the salary. */}
                      {philhealthClampNote && (
                        <p style={{ margin: "0 0 6px" }}>
                          Computed on the PhilHealth {philhealthClampNote.bound} of{" "}
                          <strong>₱{Number(philhealthClampNote.amount).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>.
                        </p>
                      )}
                      <p style={{ margin: 0, color: "var(--ui-text-muted)" }}>
                        {philhealthVersion?.circular
                          ? `Based on ${philhealthVersion.circular} effective ${formatEffectiveDate(philhealthVersion.effectiveDate)}.`
                          : `Based on the PhilHealth contribution table effective ${formatEffectiveDate(philhealthVersion?.effectiveDate)}.`}
                      </p>
                    </div>
                  </PwPopover>
                )}
              </div>
              <div className="input-group">
                <span className="input-group__addon">₱</span>
                <input
                  type="text"
                  value={formatSalary(formData.employment?.philhealthContribution || "")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, "");
                    handleInputChange("employment", "philhealthContribution", value);
                  }}
                  className={`form-input form-input--right ${
                    errors.philhealthContribution ? "error" : ""
                  }`}
                  placeholder="0.00"
                  style={{ textAlign: "right" }}
                />
              </div>
              {errors.philhealthContribution && (
                <span className="form-error-text">{errors.philhealthContribution}</span>
              )}
              {/* BL-073: clickable, salary-derived PhilHealth suggestion (mirrors
                  the SSS chip). The input stays the single source of truth;
                  clicking fills it. Absent when no version resolves or salary is
                  blank (manual entry). A legitimate ₱0.00 still renders. */}
              {suggestedPhilhealth !== null && (
                <button
                  type="button"
                  tabIndex={0}
                  onClick={() =>
                    handleInputChange("employment", "philhealthContribution", String(suggestedPhilhealth))
                  }
                  title="Click to fill the PhilHealth field with this suggestion"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    marginTop: "6px",
                    padding: "3px 8px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "var(--ui-primary)",
                    background: "var(--ui-primary-tint)",
                    border: "1px solid var(--ui-border)",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  Suggested: ₱{suggestedPhilhealth.toLocaleString("en-PH", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} · click to fill
                </button>
              )}
              {/* H11: the PhilHealth twin of C14's note — the same sentence,
                  with the agency substituted. It renders NO link, here or on
                  any other surface (H11a); "Settings → Pay Rates" is prose.
                  The input, its validator and the chip all stay:
                  the stored figure is dormant, not dead, and is the live target
                  again the moment the basis is switched off. */}
              {philhealthBasisEnabled && (
                <ContributionBasisNote agencies={["PhilHealth"]} />
              )}
            </div>

            {/* Withholding Tax Field */}
            <div className="form-group">
              <label className="form-label">
                Tax <span className="required">*</span>
              </label>
              <div className="input-group">
                <span className="input-group__addon">₱</span>
                <input
                  type="text"
                  value={formatSalary(formData.employment?.withholdingTax || "")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, "");
                    handleInputChange("employment", "withholdingTax", value);
                  }}
                  className={`form-input form-input--right ${
                    errors.withholdingTax ? "error" : ""
                  }`}
                  placeholder="0.00"
                  style={{ textAlign: "right" }}
                />
              </div>
              {errors.withholdingTax && (
                <span className="form-error-text">{errors.withholdingTax}</span>
              )}
            </div>
          </div>

          {/* Row 4: Start Date, Effective Date, Department, Include in bank export */}
          <div className="employment-form-row employment-form-row--4col">
            {/* Start Date Field */}
            <div className="form-group">
              <label className="form-label">
                Start Date <span className="required">*</span>
              </label>
              <input
                type="date"
                value={formData.employment?.startDate || ""}
                onChange={(e) =>
                  handleInputChange("employment", "startDate", e.target.value)
                }
                className={`form-input ${errors.startDate ? "error" : ""}`}
                placeholder="dd/mm/yyyy"
              />
              {errors.startDate && (
                <span className="form-error-text">{errors.startDate}</span>
              )}
              {formData.employment?.startDate && (
                <p className="form-date-label">
                  {formatDateToFilipino(formData.employment.startDate)}
                </p>
              )}
            </div>

            {/* Department Field */}
            <div className="form-group">
              <label className="form-label">
                Department
              </label>
              {deptLoading ? (
                <select className="form-select" disabled>
                  <option>Loading...</option>
                </select>
              ) : departments.length === 0 ? (
                <>
                  <select
                    className="form-select"
                    disabled
                    style={{ background: "var(--ui-bg)", color: "var(--ui-text-faint)" }}
                  >
                    <option value="">No departments available</option>
                  </select>
                  <div style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    padding: "12px 14px",
                    background: "var(--ui-warning-tint)",
                    border: "1px solid var(--ui-border)",
                    borderRadius: "8px",
                    marginTop: "8px",
                  }}>
                    <Icon name="alert-circle" size={16} style={{ flexShrink: 0, marginTop: "1px" }} />
                    <span style={{ fontSize: "13px", color: "var(--ui-warning-text)", lineHeight: "1.5" }}>
                      No Departments has been set up yet. Please go to{" "}
                      <a
                        href={`/company/${company?.slug}/settings/departments`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/company/${company?.slug}/settings/departments`);
                        }}
                        style={{ color: "var(--ui-warning-hover)", fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}
                      >
                        Settings &gt; Departments
                      </a>{" "}
                      to set it up.
                    </span>
                  </div>
                </>
              ) : (
                <select
                  value={formData.employment?.department || ""}
                  onChange={(e) =>
                    handleInputChange("employment", "department", e.target.value)
                  }
                  className={`form-select ${errors.department ? "error" : ""}`}
                >
                  <option value="">Select department</option>
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              )}
              {errors.department && (
                <span className="form-error-text">{errors.department}</span>
              )}
            </div>

            {/* Include in bank export file checkbox */}
            <div className="form-group">
              <div style={{ paddingTop: "2rem" }}>
                <label style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", fontWeight: "normal", fontSize: "0.875rem" }}>
                  <input
                    type="checkbox"
                    checked={formData.employment?.inBankExportFile !== undefined ? formData.employment.inBankExportFile : true}
                    onChange={(e) =>
                      handleInputChange("employment", "inBankExportFile", e.target.checked)
                    }
                    style={{ marginRight: "0.5rem", width: "16px", height: "16px" }}
                  />
                  <span style={{ whiteSpace: "nowrap", color: "var(--ui-text-secondary)" }}>Include in bank export file</span>
                </label>
                <small className="form-help-text" style={{ marginLeft: "1.375rem", marginTop: "0.25rem", display: "block" }}>
                  Include in salary disbursement files
                </small>
              </div>
            </div>
          </div>

          {/* Row 3: End Date and Reason - Only shown in edit mode */}
          {isEdit && (
            <div className="employment-form-row">
              {/* End Date Field */}
              <div className="form-group">
                <label className="form-label">
                  End Date
                </label>
                <input
                  type="date"
                  value={formData.employment?.endDate || ""}
                  onChange={(e) =>
                    handleInputChange("employment", "endDate", e.target.value)
                  }
                  className={`form-input ${errors.endDate ? "error" : ""}`}
                  placeholder="dd/mm/yyyy"
                />
                {errors.endDate && (
                  <span className="form-error-text">{errors.endDate}</span>
                )}
                {/* Date format confirmation label */}
                {formData.employment?.endDate && !errors.endDate && (
                  <p className="form-date-label">
                    {formatDateToFilipino(formData.employment.endDate)}
                  </p>
                )}
                {/* Clear end date — un-ends the employee (BL-075). Anchored under
                    the field it clears, and only offered once an end date exists.
                    Clearing the date field by hand used to look like it worked but
                    never persisted; this is the deliberate path, and it resets the
                    reason and notes with it. The confirm modal carries the
                    consequences, so there's no explanatory paragraph here. */}
                {formData.employment?.endDate && onClearEndDate && (
                  <button
                    type="button"
                    className="employment-clear-end-date"
                    onClick={onClearEndDate}
                  >
                    <Icon name="x" size={14} />
                    Clear end date
                  </button>
                )}
              </div>

              {/* Reason Field */}
              <div className="form-group">
                <label className="form-label">
                  Reason
                </label>
                <select
                  value={formData.employment?.endReason || ""}
                  onChange={(e) =>
                    handleInputChange("employment", "endReason", e.target.value)
                  }
                  className="form-select"
                  disabled={!formData.employment?.endDate}
                >
                  <option value="">Select reason</option>
                  <option value="resigned">Resigned</option>
                  <option value="terminated">Terminated</option>
                  <option value="other">Other</option>
                </select>
                <small className="form-help-text">
                  {formData.employment?.endDate
                    ? "Select the reason for employment end"
                    : "Set end date to enable reason selection"}
                </small>
              </div>
            </div>
          )}

          {/* Row 4: Notes - Only shown when end date is set in edit mode */}
          {isEdit && formData.employment?.endDate && (
            <div className="employment-form-row">
              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label className="form-label">
                  Notes <span style={{ fontSize: "0.875rem", color: "var(--ui-text-muted)", fontWeight: "normal" }}>({formData.employment?.notes?.length || 0}/100)</span>
                </label>
                <textarea
                  value={formData.employment?.notes || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 100) {
                      handleInputChange("employment", "notes", value);
                    }
                  }}
                  className="form-input"
                  placeholder="Additional notes about employment end (max 100 characters)"
                  rows={3}
                  maxLength={100}
                  style={{ resize: "vertical", minHeight: "80px" }}
                />
                <small className="form-help-text">
                  Provide any additional context about the employment end
                </small>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Work Schedule Section */}
      <div className="employment-details-container">
        <div className="form-section-header">
          <div className="form-section-icon"><Icon name="calendar" /></div>
          <h2 className="form-section-title">Work Schedule</h2>
        </div>

        {/* Working Days + Scheduled Hours side by side */}
        <div className="ws-row">
          <div className="form-group ws-days">
            <label className="form-label">
              Working Days <span className="required">*</span>
            </label>
            <div style={{ display: "flex", gap: "0.375rem" }}>
              {[
                { id: "sunday", label: "Sun" },
                { id: "monday", label: "Mon" },
                { id: "tuesday", label: "Tue" },
                { id: "wednesday", label: "Wed" },
                { id: "thursday", label: "Thu" },
                { id: "friday", label: "Fri" },
                { id: "saturday", label: "Sat" },
              ].map((day) => {
                const isSelected = formData.employment?.workSchedule?.[day.id]?.isWorkingDay || false;
                return (
                  <button
                    key={day.id}
                    type="button"
                    onClick={() => {
                      const currentSchedule = formData.employment?.workSchedule || {};
                      const updatedSchedule = {
                        ...currentSchedule,
                        [day.id]: {
                          ...currentSchedule[day.id],
                          isWorkingDay: !isSelected,
                          startTime: currentSchedule[day.id]?.startTime || scheduleDefaults.startTime,
                          endTime: currentSchedule[day.id]?.endTime || scheduleDefaults.endTime,
                          breakDuration: currentSchedule[day.id]?.breakDuration || 60,
                        },
                      };
                      handleInputChange("employment", "workSchedule", updatedSchedule);
                    }}
                    style={{
                      padding: "0.4rem 0.75rem",
                      borderRadius: "6px",
                      border: isSelected ? "none" : "1px solid var(--ui-border)",
                      background: isSelected ? "var(--ui-primary)" : "white",
                      color: isSelected ? "white" : "var(--ui-text-muted)",
                      fontSize: "0.8125rem",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
            {errors.workSchedule && (
              <span className="form-error-text">{errors.workSchedule}</span>
            )}
          </div>

          {/* Scheduled Hours — only shown when at least one working day is selected */}
          {Object.values(formData.employment?.workSchedule || {}).some(d => d?.isWorkingDay) && (
            <div className="form-group ws-hours">
              <label className="form-label">Scheduled Hours <span className="required">*</span></label>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <input
                  type="time"
                  value={(() => {
                    const schedule = formData.employment?.workSchedule || {};
                    const firstWorkDay = Object.values(schedule).find(d => d?.isWorkingDay);
                    return firstWorkDay?.startTime || scheduleDefaults.startTime;
                  })()}
                  onChange={(e) => {
                    const newStartTime = e.target.value;
                    const currentSchedule = formData.employment?.workSchedule || {};
                    const DAYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
                    const updated = Object.fromEntries(
                      DAYS.map(day => [
                        day,
                        { ...(currentSchedule[day] || { isWorkingDay: false, breakDuration: 60, endTime: scheduleDefaults.endTime }),
                          startTime: newStartTime }
                      ])
                    );
                    handleInputChange("employment", "workSchedule", updated);
                  }}
                  className="ws-time-input"
                />
                <span style={{ color: "var(--ui-text-muted)", fontSize: "0.8125rem", flexShrink: 0 }}>to</span>
                <input
                  type="time"
                  value={(() => {
                    const schedule = formData.employment?.workSchedule || {};
                    const firstWorkDay = Object.values(schedule).find(d => d?.isWorkingDay);
                    return firstWorkDay?.endTime || scheduleDefaults.endTime;
                  })()}
                  onChange={(e) => {
                    const newEndTime = e.target.value;
                    const currentSchedule = formData.employment?.workSchedule || {};
                    const DAYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
                    const updated = Object.fromEntries(
                      DAYS.map(day => [
                        day,
                        { ...(currentSchedule[day] || { isWorkingDay: false, breakDuration: 60, startTime: scheduleDefaults.startTime }),
                          endTime: newEndTime }
                      ])
                    );
                    handleInputChange("employment", "workSchedule", updated);
                  }}
                  className="ws-time-input"
                />
                <span style={{ width: "1px", height: "20px", background: "var(--ui-border)", flexShrink: 0 }}></span>
                <span style={{ color: "var(--ui-text-muted)", fontSize: "0.8125rem", flexShrink: 0 }}>break</span>
                <select
                  value={(() => {
                    const schedule = formData.employment?.workSchedule || {};
                    const firstWorkDay = Object.values(schedule).find(d => d?.isWorkingDay);
                    return firstWorkDay?.breakDuration ?? 60;
                  })()}
                  onChange={(e) => {
                    const newBreak = parseInt(e.target.value, 10);
                    const currentSchedule = formData.employment?.workSchedule || {};
                    const DAYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
                    const updated = Object.fromEntries(
                      DAYS.map(day => [
                        day,
                        { ...(currentSchedule[day] || { isWorkingDay: false, startTime: scheduleDefaults.startTime, endTime: scheduleDefaults.endTime }),
                          breakDuration: newBreak }
                      ])
                    );
                    handleInputChange("employment", "workSchedule", updated);
                  }}
                  className="ws-break-select"
                >
                  <option value={0}>No break</option>
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Merged summary bar: schedule info + OT/UT */}
        <div className="ws-summary-bar" style={{
          background: scheduleOtUt && (scheduleOtUt.ot > 0 || scheduleOtUt.ut > 0) ? "var(--ui-warning-tint)" : "var(--ui-bg)",
        }}>
          <span style={{ color: "var(--ui-text-muted)" }}>Work Days: </span>
          <span style={{ color: "var(--ui-primary)", fontWeight: "600" }}>
            {Object.values(formData.employment?.workSchedule || {}).filter(d => d?.isWorkingDay).length} days/week
          </span>
          <span style={{ color: "var(--ui-border)", margin: "0 0.25rem" }}>|</span>
          <span style={{ color: "var(--ui-text-muted)" }}>Rest: </span>
          <span style={{ color: "var(--ui-primary)", fontWeight: "600" }}>
            {(() => {
              const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
              const labels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
              const restDays = days
                .map((d, i) => (!formData.employment?.workSchedule?.[d]?.isWorkingDay ? labels[i] : null))
                .filter(Boolean);
              return restDays.length > 0 ? restDays.join(", ") : "None";
            })()}
          </span>
          {scheduleOtUt && (
            <>
              <span style={{ color: "var(--ui-border)", margin: "0 0.25rem" }}>|</span>
              <span style={{ color: "var(--ui-text-muted)" }}>Regular: </span>
              <span style={{ fontWeight: "600", color: "var(--ui-text)" }}>{scheduleOtUt.regularHours}h</span>
              <span style={{ color: "var(--ui-border)", margin: "0 0.25rem" }}>|</span>
              <span style={{ color: "var(--ui-text-muted)" }}>OT: </span>
              <span style={{ fontWeight: "600", color: scheduleOtUt.ot > 0 ? "var(--ui-warning-hover)" : "var(--ui-text)" }}>
                {scheduleOtUt.ot.toFixed(2)}h
              </span>
              <span style={{ color: "var(--ui-border)", margin: "0 0.25rem" }}>|</span>
              <span style={{ color: "var(--ui-text-muted)" }}>UT: </span>
              <span style={{ fontWeight: "600", color: scheduleOtUt.ut > 0 ? "var(--ui-danger)" : "var(--ui-text)" }}>
                {scheduleOtUt.ut.toFixed(2)}h
              </span>
              {hoursPerDay && (
                <span style={{ color: "var(--ui-text-faint)", fontSize: "0.75rem", marginLeft: "auto" }}>
                  Based on {hoursPerDay}h/day
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Bank Details moved to Step 3 (Sensitive Info) per v2.0 PII split. */}

      {/* Philippine Labor Law Compliance Box */}
      <div className="employment-compliance-box">
        <div className="employment-compliance-box__header">
          <span className="employment-compliance-box__flag"><Icon name="command" size={20} /></span>
          <h3 className="employment-compliance-box__title">
            Philippine Labor Law Compliance
          </h3>
        </div>
        <ul className="employment-compliance-box__list">
          <li>
            <strong>Minimum Wage:</strong> Ensure salary meets regional minimum
            wage requirements
          </li>
          <li>
            <strong>13th Month Pay:</strong> Generate it from Adjustments →
            Generate 13th Month, due on or before December 24
          </li>
          <li>
            <strong>Government Benefits:</strong> SSS, PhilHealth, and Pag-IBIG
            contributions will be automatically computed
          </li>
          <li>
            <strong>Probationary Period:</strong> Maximum 6 months for regular
            positions per Labor Code
          </li>
        </ul>
      </div>

      {/* Form Actions */}
      <div className="add-employee-form-actions">
        <button
          onClick={handlePrevious}
          className="btn btn--secondary"
          type="button"
        >
          <Icon name="arrow-left" size={14} aria-hidden="true" /> Previous
        </button>
        <button
          onClick={handleNext}
          className="btn btn--primary"
          type="button"
          disabled={!isFormValid()}
        >
          {nextStepLabel}
        </button>
      </div>
    </div>
  );
};
